import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useModal } from '../context/ModalDialogContext';
import AddressAutocompleteInput from './AddressAutocompleteInput';
import PaginationBar from './PaginationBar';

// Custom SVG Icons
const Icons = {
  Truck: ({ size = 18, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <rect x="1" y="3" width="15" height="13" />
      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
      <circle cx="5.5" cy="18.5" r="2.5" />
      <circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  ),
  Building: ({ size = 18, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
      <path d="M9 22v-4h6v4" />
      <path d="M8 6h.01" /><path d="M16 6h.01" />
      <path d="M8 10h.01" /><path d="M16 10h.01" />
      <path d="M8 14h.01" /><path d="M16 14h.01" />
    </svg>
  ),
  Users: ({ size = 18, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  UserPlus: ({ size = 16, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" />
    </svg>
  ),
  UserMinus: ({ size = 16, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <line x1="22" y1="11" x2="16" y2="11" />
    </svg>
  ),
  ShoppingBag: ({ size = 18, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  ),
  DollarSign: ({ size = 18, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  Plus: ({ size = 16, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  Search: ({ size = 16, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  Edit: ({ size = 15, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  Trash: ({ size = 15, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  ),
  Close: ({ size = 16, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Globe: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
  Mail: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  ExternalLink: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  ),
  MapPin: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  ),
  Check: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  Coin: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 6v12" />
      <path d="M15 9.5a3.5 3.5 0 0 0-6 0c0 2 3 2.5 3 4.5a3.5 3.5 0 0 1-6 0" />
    </svg>
  )
};

const formatErrorDetail = (detail, fallback = 'An unexpected error occurred.') => {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(item => {
      if (typeof item === 'string') return item;
      if (item && item.msg) return item.msg;
      return JSON.stringify(item);
    }).join(', ');
  }
  if (typeof detail === 'object') {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return String(detail);
};

export default function CompanyManager({
  user,
  tenantConfig,
  isGlobalRole,
  isAdminRole,
  tenants = [],
  selectedTenantId,
  setSelectedTenantId
}) {
  const modal = useModal();
  const isDev = typeof window !== 'undefined' && window.location.port === '3000';
  const API_BASE = isDev ? `http://${window.location.hostname}:8000` : '';
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');

  // State: Tenant Scoping
  const [activeTenantId, setActiveTenantId] = useState(
    selectedTenantId || (isGlobalRole ? '' : (tenantConfig?.id ? tenantConfig.id.toString() : ''))
  );

  useEffect(() => {
    if (selectedTenantId !== undefined && selectedTenantId !== null) {
      setActiveTenantId(selectedTenantId);
    }
  }, [selectedTenantId]);

  const handleTenantChange = (newTenantId) => {
    setActiveTenantId(newTenantId);
    if (setSelectedTenantId) {
      setSelectedTenantId(newTenantId);
    }
  };

  // State: Companies Data
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name_asc'); // name_asc, name_desc, users, orders, revenue, newest
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState('success');

  // State: Selection & Unified Tabbed Modal
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [activeCompanyTab, setActiveCompanyTab] = useState('details'); // 'details' | 'address' | 'users' | 'account'

  const selectedCompany = useMemo(() => {
    return companies.find((c) => c.id === selectedCompanyId) || null;
  }, [companies, selectedCompanyId]);

  // State: Modals
  const [editingCompany, setEditingCompany] = useState(null); // null when closed, {} for new, or company obj
  const [isNewCompany, setIsNewCompany] = useState(false);
  const [companyModalLoading, setCompanyModalLoading] = useState(false);
  const [companyModalError, setCompanyModalError] = useState('');

  // Manage Users State
  const [companyUsers, setCompanyUsers] = useState([]);
  const [tenantAvailableUsers, setTenantAvailableUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userActionError, setUserActionError] = useState('');
  const [userAssignMode, setUserAssignMode] = useState('existing'); // 'existing' | 'new'
  const [selectedAssignUserId, setSelectedAssignUserId] = useState('');
  const [newUserData, setNewUserData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    phone_number: '',
    role: 'OPERATOR'
  });

  // Manage Orders State
  const [companyOrders, setCompanyOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  // Delete Confirmation Modal State
  const [deletingCompany, setDeletingCompany] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Form State for Add/Edit Company
  const [formData, setFormData] = useState({
    name: '',
    tenant_id: '',
    billing_address: '',
    billing_city: '',
    billing_state: '',
    billing_zip: '',
    billing_country: '',
    website_address: '',
    bookings_link: '',
    social_media: '',
    enquiries_email: '',
    logo_url: ''
  });
  const [logoUploading, setLogoUploading] = useState(false);

  // Delivery Addresses State for Add/Edit Company Modal
  const [companyDeliveryAddresses, setCompanyDeliveryAddresses] = useState([]);
  const [deliveryAddressesLoading, setDeliveryAddressesLoading] = useState(false);
  const [showAddDeliveryForm, setShowAddDeliveryForm] = useState(false);
  const [deliveryForm, setDeliveryForm] = useState({
    label: '',
    recipient_name: '',
    phone: '',
    street: '',
    street2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    country_code: '',
    latitude: null,
    longitude: null,
    delivery_instructions: '',
    is_default: false
  });
  const [deliverySaving, setDeliverySaving] = useState(false);
  const [deliveryError, setDeliveryError] = useState('');

  // Pagination & Debounce states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCompanies, setTotalCompanies] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [serverKpis, setServerKpis] = useState(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search query
  useEffect(() => {
    if (searchQuery === debouncedSearch) return;
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, debouncedSearch]);

  // Reset page when search, tenant, or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, activeTenantId, sortBy]);

  // Helper: Trigger Toast Notification
  const showToast = (msg, type = 'success') => {
    const formatted = typeof msg === 'string' ? msg : formatErrorDetail(msg);
    setToastMessage(formatted);
    setToastType(type);
    if (modal?.notify) modal.notify(formatted, type);
    setTimeout(() => {
      setToastMessage(null);
    }, 4000);
  };

  // 1. Fetch Companies List with pagination
  const fetchCompanies = async (pageOverride, sizeOverride) => {
    if (!token) return;
    setLoading(true);
    try {
      const pageToUse = pageOverride !== undefined ? pageOverride : currentPage;
      const sizeToUse = sizeOverride !== undefined ? sizeOverride : pageSize;

      const params = new URLSearchParams();
      params.append('page', pageToUse);
      params.append('limit', sizeToUse);
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (isGlobalRole && activeTenantId) params.append('tenant_id', activeTenantId);
      if (sortBy) params.append('sort_by', sortBy);

      const res = await fetch(`${API_BASE}/api/v1/organizations?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) {
          setCompanies(data || []);
          setTotalCompanies(data.length);
          setTotalPages(Math.max(1, Math.ceil(data.length / sizeToUse)));
          setServerKpis(null);
        } else if (data && Array.isArray(data.organizations)) {
          setCompanies(data.organizations || []);
          setTotalCompanies(data.total !== undefined ? data.total : data.organizations.length);
          setTotalPages(data.total_pages || Math.max(1, Math.ceil((data.total || data.organizations.length) / sizeToUse)));
          if (data.kpis) {
            setServerKpis(data.kpis);
          }
        }
      } else {
        const err = await res.json().catch(() => ({}));
        showToast(formatErrorDetail(err.detail, 'Failed to load companies.'), 'error');
      }
    } catch (err) {
      console.error('[CompanyManager] Fetch error:', err);
      showToast('Network error loading companies.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, activeTenantId, currentPage, pageSize, debouncedSearch, sortBy]);

  // 2. Filter & Sort Companies
  const filteredCompanies = useMemo(() => {
    if (serverKpis || (totalCompanies > 0 && companies.length <= pageSize)) {
      return companies;
    }
    let result = [...companies];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(c => 
        (c.name && c.name.toLowerCase().includes(q)) ||
        (c.billing_city && c.billing_city.toLowerCase().includes(q)) ||
        (c.billing_country && c.billing_country.toLowerCase().includes(q)) ||
        (c.enquiries_email && c.enquiries_email.toLowerCase().includes(q)) ||
        (c.website_address && c.website_address.toLowerCase().includes(q)) ||
        (c.tenant_name && c.tenant_name.toLowerCase().includes(q))
      );
    }

    result.sort((a, b) => {
      if (sortBy === 'name_asc') return (a.name || '').localeCompare(b.name || '');
      if (sortBy === 'name_desc') return (b.name || '').localeCompare(a.name || '');
      if (sortBy === 'users') return (b.users_count || 0) - (a.users_count || 0);
      if (sortBy === 'orders') return (b.orders_count || 0) - (a.orders_count || 0);
      if (sortBy === 'revenue') return (b.total_spent || 0) - (a.total_spent || 0);
      if (sortBy === 'newest') return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      return 0;
    });

    return result;
  }, [companies, searchQuery, sortBy, serverKpis, totalCompanies, pageSize]);

  // KPI Aggregates
  const kpis = useMemo(() => {
    if (serverKpis) {
      return {
        totalCompanies: serverKpis.total_companies || 0,
        totalUsers: serverKpis.total_users || 0,
        totalOrders: serverKpis.total_orders || 0,
        totalVolume: serverKpis.total_volume || 0
      };
    }
    const totalCount = totalCompanies || companies.length;
    const totalUsers = companies.reduce((acc, c) => acc + (c.users_count || 0), 0);
    const totalOrders = companies.reduce((acc, c) => acc + (c.orders_count || 0), 0);
    const totalVolume = companies.reduce((acc, c) => acc + (c.total_spent || 0), 0);
    return { totalCompanies: totalCount, totalUsers, totalOrders, totalVolume };
  }, [companies, totalCompanies, serverKpis]);

  // 3. Open Add/Edit Modal
  const handleOpenAddModal = () => {
    setIsNewCompany(true);
    setActiveCompanyTab('details');
    setSelectedCompanyId(null);
    setCompanyUsers([]);
    setCompanyOrders([]);
    setFormData({
      name: '',
      tenant_id: activeTenantId || (tenantConfig?.id ? tenantConfig.id.toString() : ''),
      billing_address: '',
      billing_city: '',
      billing_state: '',
      billing_zip: '',
      billing_country: '',
      website_address: '',
      bookings_link: '',
      social_media: '',
      enquiries_email: '',
      logo_url: ''
    });
    setCompanyDeliveryAddresses([]);
    setShowAddDeliveryForm(false);
    setDeliveryError('');
    setDeliveryForm({
      label: '',
      recipient_name: '',
      phone: '',
      street: '',
      street2: '',
      city: '',
      state: '',
      zip: '',
      country: '',
      country_code: '',
      latitude: null,
      longitude: null,
      delivery_instructions: '',
      is_default: false
    });
    setCompanyModalError('');
    setEditingCompany({});
  };

  const handleOpenEditModal = (comp, initialTab = 'details') => {
    setIsNewCompany(false);
    setSelectedCompanyId(comp.id);
    setActiveCompanyTab(initialTab);
    setFormData({
      name: comp.name || '',
      tenant_id: comp.tenant_id ? comp.tenant_id.toString() : '',
      billing_address: comp.billing_address || '',
      billing_city: comp.billing_city || '',
      billing_state: comp.billing_state || '',
      billing_zip: comp.billing_zip || '',
      billing_country: comp.billing_country || '',
      website_address: comp.website_address || '',
      bookings_link: comp.bookings_link || '',
      social_media: comp.social_media || '',
      enquiries_email: comp.enquiries_email || '',
      logo_url: comp.logo_url || ''
    });
    setCompanyDeliveryAddresses([]);
    setShowAddDeliveryForm(false);
    setDeliveryError('');
    setDeliveryForm({
      label: '',
      recipient_name: '',
      phone: '',
      street: '',
      street2: '',
      city: '',
      state: '',
      zip: '',
      country: '',
      country_code: '',
      latitude: null,
      longitude: null,
      delivery_instructions: '',
      is_default: false
    });
    setCompanyModalError('');
    setEditingCompany(comp);
    if (comp.id) {
      fetchCompanyDeliveryAddresses(comp.id);
      fetchCompanyUsersData(comp);
      fetchCompanyOrdersData(comp);
    }
  };

  // Delivery Addresses Handlers
  const fetchCompanyDeliveryAddresses = async (orgId) => {
    if (!orgId || !token) return;
    setDeliveryAddressesLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/organizations/${orgId}/addresses`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (res.ok) {
        const data = await res.json();
        setCompanyDeliveryAddresses(data || []);
      }
    } catch (err) {
      console.warn('Failed to load company delivery addresses:', err);
    } finally {
      setDeliveryAddressesLoading(false);
    }
  };

  const handleCopyBillingToDelivery = () => {
    if (!formData.billing_address && !formData.billing_city) {
      setDeliveryError('Billing address is currently empty. Please fill in billing address or use smart fill-in first.');
      return;
    }
    setDeliveryForm(prev => ({
      ...prev,
      street: formData.billing_address || '',
      city: formData.billing_city || '',
      state: formData.billing_state || '',
      zip: formData.billing_zip || '',
      country: formData.billing_country || ''
    }));
    setDeliveryError('');
  };

  const handleSaveDeliveryAddress = async (e) => {
    if (e) e.preventDefault();
    if (!deliveryForm.street.trim() || !deliveryForm.city.trim() || !deliveryForm.zip.trim() || !deliveryForm.country.trim()) {
      setDeliveryError('Street address, city, postal/zip, and country are required.');
      return;
    }
    setDeliverySaving(true);
    setDeliveryError('');

    const newAddrPayload = {
      label: deliveryForm.label.trim() || null,
      recipient_name: deliveryForm.recipient_name.trim() || null,
      phone: deliveryForm.phone.trim() || null,
      street: deliveryForm.street.trim(),
      street2: deliveryForm.street2.trim() || null,
      city: deliveryForm.city.trim(),
      state: deliveryForm.state.trim() || null,
      zip: deliveryForm.zip.trim(),
      country: deliveryForm.country.trim(),
      country_code: deliveryForm.country_code.trim().toUpperCase() || null,
      latitude: deliveryForm.latitude,
      longitude: deliveryForm.longitude,
      delivery_instructions: deliveryForm.delivery_instructions.trim() || null,
      is_default: Boolean(deliveryForm.is_default || companyDeliveryAddresses.length === 0)
    };

    if (editingCompany && editingCompany.id) {
      try {
        const res = await fetch(`${API_BASE}/api/v1/organizations/${editingCompany.id}/addresses`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'X-Tenant-Domain': window.location.hostname
          },
          body: JSON.stringify(newAddrPayload)
        });
        if (res.ok) {
          showToast('Delivery address added successfully.');
          setShowAddDeliveryForm(false);
          setDeliveryForm({
            label: '',
            recipient_name: '',
            phone: '',
            street: '',
            street2: '',
            city: '',
            state: '',
            zip: '',
            country: '',
            country_code: '',
            latitude: null,
            longitude: null,
            delivery_instructions: '',
            is_default: false
          });
          fetchCompanyDeliveryAddresses(editingCompany.id);
        } else {
          const errData = await res.json().catch(() => ({}));
          setDeliveryError(formatErrorDetail(errData.detail, 'Failed to add delivery address.'));
        }
      } catch (err) {
        setDeliveryError(err.message || 'Error adding delivery address.');
      } finally {
        setDeliverySaving(false);
      }
    } else {
      const stagedAddr = {
        ...newAddrPayload,
        id: `staged_${Date.now()}`
      };
      setCompanyDeliveryAddresses(prev => {
        let updated = [...prev];
        if (stagedAddr.is_default) {
          updated = updated.map(a => ({ ...a, is_default: false }));
        }
        return [...updated, stagedAddr];
      });
      setShowAddDeliveryForm(false);
      setDeliveryForm({
        label: '',
        recipient_name: '',
        phone: '',
        street: '',
        street2: '',
        city: '',
        state: '',
        zip: '',
        country: '',
        country_code: '',
        latitude: null,
        longitude: null,
        delivery_instructions: '',
        is_default: false
      });
      setDeliverySaving(false);
    }
  };

  const handleDeleteCompanyAddress = async (addrId) => {
    const confirmed = await modal.confirm('Are you sure you want to delete this delivery address?', {
      title: 'Delete Delivery Address',
      confirmText: 'Delete Address',
      danger: true
    });
    if (!confirmed) return;
    if (editingCompany && editingCompany.id && !String(addrId).startsWith('staged_')) {
      try {
        const res = await fetch(`${API_BASE}/api/v1/organizations/${editingCompany.id}/addresses/${addrId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Tenant-Domain': window.location.hostname
          }
        });
        if (res.ok) {
          showToast('Delivery address removed.');
          fetchCompanyDeliveryAddresses(editingCompany.id);
        } else {
          showToast('Failed to delete address.', 'error');
        }
      } catch (err) {
        showToast('Error deleting delivery address.', 'error');
      }
    } else {
      setCompanyDeliveryAddresses(prev => {
        const filtered = prev.filter(a => a.id !== addrId);
        if (filtered.length > 0 && !filtered.some(a => a.is_default)) {
          filtered[0].is_default = true;
        }
        return filtered;
      });
    }
  };

  const handleSetDefaultCompanyAddress = async (addrId) => {
    if (editingCompany && editingCompany.id && !String(addrId).startsWith('staged_')) {
      try {
        const res = await fetch(`${API_BASE}/api/v1/organizations/${editingCompany.id}/addresses/${addrId}/default`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Tenant-Domain': window.location.hostname
          }
        });
        if (res.ok) {
          showToast('Default delivery address updated.');
          fetchCompanyDeliveryAddresses(editingCompany.id);
        }
      } catch (err) {
        showToast('Error updating default address.', 'error');
      }
    } else {
      setCompanyDeliveryAddresses(prev =>
        prev.map(a => ({ ...a, is_default: a.id === addrId }))
      );
    }
  };

  // Logo Upload Handler
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoUploading(true);
    setCompanyModalError('');

    const uploadData = new FormData();
    uploadData.append('file', file);

    try {
      const res = await fetch(`${API_BASE}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: uploadData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatErrorDetail(data.detail, 'Failed to upload logo image.'));
      const relativePath = data.file.location;
      const fullUrl = `${API_BASE}${relativePath}`;
      setFormData(prev => ({ ...prev, logo_url: fullUrl }));
    } catch (err) {
      setCompanyModalError(err.message);
    } finally {
      setLogoUploading(false);
    }
  };

  // 3. Save Company Details (Create or Update)
  const handleSaveCompany = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!formData.name.trim()) {
      setCompanyModalError('Company Name is required.');
      return;
    }

    setCompanyModalLoading(true);
    setCompanyModalError('');

    try {
      const isNewCompany = !editingCompany || !editingCompany.id;
      const payload = {
        name: formData.name.trim(),
        tenant_id: formData.tenant_id ? parseInt(formData.tenant_id) : undefined,
        bookings_link: formData.bookings_link || null,
        website_address: formData.website_address || null,
        phone_number: formData.phone_number || null,
        enquiries_email: formData.enquiries_email || null,
        logo_url: formData.logo_url || null,
        social_media: formData.social_media || null,
        billing_address: formData.billing_address || null,
        billing_city: formData.billing_city || null,
        billing_state: formData.billing_state || null,
        billing_zip: formData.billing_zip || null,
        billing_country: formData.billing_country || null
      };

      let res;
      if (isNewCompany) {
        res = await fetch(`${API_BASE}/api/v1/organizations`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'X-Tenant-Domain': window.location.hostname
          },
          body: JSON.stringify(payload)
        });
      } else {
        res = await fetch(`${API_BASE}/api/v1/organizations/${editingCompany.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'X-Tenant-Domain': window.location.hostname
          },
          body: JSON.stringify(payload)
        });
      }

      const resData = await res.json();
      if (!res.ok) {
        throw new Error(formatErrorDetail(resData.detail, 'Failed to save company details.'));
      }

      // If new company created and there were staged delivery addresses, save them
      if (isNewCompany && companyDeliveryAddresses.length > 0 && resData.id) {
        for (const stagedAddr of companyDeliveryAddresses) {
          try {
            await fetch(`${API_BASE}/api/v1/organizations/${resData.id}/addresses`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                'X-Tenant-Domain': window.location.hostname
              },
              body: JSON.stringify({
                label: stagedAddr.label || null,
                recipient_name: stagedAddr.recipient_name || null,
                phone: stagedAddr.phone || null,
                street: stagedAddr.street,
                street2: stagedAddr.street2 || null,
                city: stagedAddr.city,
                state: stagedAddr.state || null,
                zip: stagedAddr.zip,
                country: stagedAddr.country,
                country_code: stagedAddr.country_code || null,
                latitude: stagedAddr.latitude,
                longitude: stagedAddr.longitude,
                delivery_instructions: stagedAddr.delivery_instructions || null,
                is_default: Boolean(stagedAddr.is_default)
              })
            });
          } catch (postErr) {
            console.warn('Failed to save staged delivery address:', postErr);
          }
        }
      }

      showToast(isNewCompany ? `Company "${formData.name}" created successfully!` : 'Company details updated successfully!');
      setEditingCompany(null);
      fetchCompanies();
    } catch (err) {
      setCompanyModalError(err.message);
    } finally {
      setCompanyModalLoading(false);
    }
  };

  // 4. Delete Company Handler
  const handleDeleteCompany = async () => {
    if (!deletingCompany) return;
    setDeleteLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/organizations/${deletingCompany.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (res.ok) {
        showToast(`Company "${deletingCompany.name}" deleted successfully.`);
        setDeletingCompany(null);
        fetchCompanies();
      } else {
        const err = await res.json().catch(() => ({}));
        showToast(formatErrorDetail(err.detail, 'Failed to delete company.'), 'error');
      }
    } catch (err) {
      showToast('Network error deleting company.', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  // 5. Manage Users & Operators Data
  const fetchCompanyUsersData = async (comp) => {
    if (!comp || !comp.id || !token) return;
    setUserActionError('');
    setSelectedAssignUserId('');
    setUserAssignMode('existing');
    setUsersLoading(true);

    try {
      // 1. Fetch assigned users
      const usersRes = await fetch(`${API_BASE}/api/v1/organizations/${comp.id}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      const usersData = await usersRes.json();
      setCompanyUsers(usersRes.ok ? usersData : []);

      // 2. Fetch all tenant users to allow assignment
      const allUsersRes = await fetch(`${API_BASE}/api/v1/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (allUsersRes.ok) {
        const allUsersData = await allUsersRes.json();
        // Filter users in the same tenant who aren't already in this company
        const assignedIds = new Set((usersData || []).map(u => u.id));
        const available = (allUsersData || []).filter(u => {
          if (assignedIds.has(u.id)) return false;
          if (comp.tenant_id && u.tenant_id && u.tenant_id !== comp.tenant_id) return false;
          return true;
        });
        setTenantAvailableUsers(available);
      }
    } catch (err) {
      setUserActionError('Failed to load company operators.');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleOpenUsersModal = (comp) => {
    handleOpenEditModal(comp, 'users');
  };

  // Add User to Company
  const handleAddUserToCompany = async (e) => {
    e.preventDefault();
    const targetComp = editingCompany;
    if (!targetComp || !targetComp.id) return;
    setUserActionError('');

    try {
      let payload = {};
      if (userAssignMode === 'existing') {
        if (!selectedAssignUserId) {
          setUserActionError('Please select a user to assign.');
          return;
        }
        payload = { user_id: parseInt(selectedAssignUserId) };
      } else {
        if (!newUserData.email || !newUserData.password) {
          setUserActionError('Email and password are required to create a new user.');
          return;
        }
        payload = { ...newUserData };
      }

      const res = await fetch(`${API_BASE}/api/v1/organizations/${targetComp.id}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(formatErrorDetail(data.detail, 'Failed to assign user to company.'));
      }

      showToast(`User ${data.email} assigned to ${targetComp.name}!`);
      // Refresh users data & company list
      fetchCompanyUsersData(targetComp);
      fetchCompanies();
      setSelectedAssignUserId('');
      setNewUserData({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        phone_number: '',
        role: 'OPERATOR'
      });
    } catch (err) {
      setUserActionError(err.message);
    }
  };

  // Remove User from Company
  const handleRemoveUserFromCompany = async (userId, userEmail) => {
    const targetComp = editingCompany;
    if (!targetComp || !targetComp.id) return;
    const confirmed = await modal.confirm(`Are you sure you want to remove ${userEmail} from ${targetComp.name}?`, {
      title: 'Remove User from Company',
      confirmText: 'Remove User',
      danger: true
    });
    if (!confirmed) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/v1/organizations/${targetComp.id}/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatErrorDetail(err.detail, 'Failed to unlink user.'));
      }

      showToast(`User ${userEmail} removed from ${targetComp.name}.`);
      fetchCompanyUsersData(targetComp);
      fetchCompanies();
    } catch (err) {
      setUserActionError(err.message);
    }
  };

  // 6. View Orders Data Handler
  const fetchCompanyOrdersData = async (comp) => {
    if (!comp || !comp.id || !token) return;
    setOrdersLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/organizations/${comp.id}/orders`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (res.ok) {
        const data = await res.json();
        setCompanyOrders(data.orders || []);
      } else {
        showToast('Failed to load company orders.', 'error');
      }
    } catch (err) {
      console.error('[CompanyManager] Fetch orders error:', err);
      showToast('Network error loading company orders.', 'error');
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleOpenOrdersModal = (comp) => {
    handleOpenEditModal(comp, 'account');
  };

  // Lock scroll on open modal
  const hasOpenModal = !!(editingCompany || deletingCompany);
  useEffect(() => {
    if (!hasOpenModal) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setEditingCompany(null);
        setDeletingCompany(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [hasOpenModal]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%', boxSizing: 'border-box' }}>
      
      {/* Toast Notification Alert */}
      {toastMessage && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          zIndex: 9999999,
          backgroundColor: toastType === 'error' ? 'rgba(239, 68, 68, 0.95)' : 'rgba(16, 185, 129, 0.95)',
          color: '#ffffff',
          padding: '12px 20px',
          borderRadius: '8px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          fontWeight: 600,
          fontSize: '13px',
          backdropFilter: 'blur(8px)'
        }}>
          {toastType === 'error' ? '⚠️' : '✓'} {typeof toastMessage === 'string' ? toastMessage : JSON.stringify(toastMessage)}
        </div>
      )}

      {/* Top Header & Filter Controls */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '15px',
        background: 'var(--card-bg)',
        border: '1px solid var(--border-color)',
        borderRadius: '8px',
        padding: '16px 20px',
        boxShadow: 'var(--card-shadow)'
      }}>
        <div>
          <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.9, color: 'var(--primary-color)' }}>
              <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
              <path d="M9 22v-4h6v4" />
              <path d="M8 6h.01" /><path d="M16 6h.01" />
              <path d="M8 10h.01" /><path d="M16 10h.01" />
              <path d="M8 14h.01" /><path d="M16 14h.01" />
            </svg>
            Company Manager
          </h4>
          <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-mute)' }}>
            Manage registered charter companies, outfitters, and corporate partners. Configure brand assets, assign operators, and track order histories.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {/* Tenant Scope Selector (Global Admins) */}
          {isGlobalRole && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: 'var(--input-bg)',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              padding: '5px 10px'
            }}>
              <Icons.Globe size={13} style={{ color: 'var(--primary-color)' }} />
              <span style={{ fontSize: '11px', color: 'var(--text-mute)', fontWeight: 600 }}>Tenant:</span>
              <select
                value={activeTenantId}
                onChange={(e) => handleTenantChange(e.target.value)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--input-text, var(--text-main))',
                  fontSize: '12px',
                  fontWeight: 500,
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="">All Tenants (Global)</option>
                {tenants.map(t => (
                  <option key={t.id} value={t.id.toString()}>
                    {t.brand_name || t.domain_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Count Badge Pill */}
          <span style={{
            backgroundColor: 'rgba(160, 196, 255, 0.15)',
            color: '#a0c4ff',
            padding: '5px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '600'
          }}>
            {filteredCompanies.length} {filteredCompanies.length === 1 ? 'Company' : 'Companies'}
          </span>

          {/* Search Box */}
          <input
            type="text"
            placeholder="Search companies, cities, emails..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              backgroundColor: 'var(--input-bg)',
              color: 'var(--input-text, var(--text-main))',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              padding: '6px 12px',
              fontSize: '12px',
              outline: 'none',
              minWidth: '200px'
            }}
          />

          {/* Sort Dropdown */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              backgroundColor: 'var(--input-bg)',
              border: '1px solid var(--border-color)',
              color: 'var(--input-text, var(--text-main))',
              borderRadius: '6px',
              padding: '6px 10px',
              fontSize: '12px',
              outline: 'none',
              cursor: 'pointer'
            }}
          >
            <option value="name_asc">Name (A → Z)</option>
            <option value="name_desc">Name (Z → A)</option>
            <option value="users">Most Operators</option>
            <option value="orders">Most Orders</option>
            <option value="revenue">Highest Spend</option>
            <option value="newest">Newest First</option>
          </select>

          {/* Open Selected Company Action Button */}
          <button
            type="button"
            data-testid="open-selected-company-btn"
            disabled={!selectedCompany}
            onClick={() => selectedCompany && handleOpenEditModal(selectedCompany, 'details')}
            title={selectedCompany ? `Open details for ${selectedCompany.name}` : 'Select a company from the list to open'}
            style={{
              backgroundColor: selectedCompany ? 'rgba(56, 139, 253, 0.15)' : 'var(--input-bg)',
              color: selectedCompany ? '#58a6ff' : 'var(--text-mute)',
              border: selectedCompany ? '1px solid rgba(56, 139, 253, 0.4)' : '1px solid var(--border-color)',
              borderRadius: '6px',
              padding: '8px 16px',
              fontSize: '12px',
              fontWeight: '700',
              cursor: selectedCompany ? 'pointer' : 'not-allowed',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              opacity: selectedCompany ? 1 : 0.6,
              boxShadow: selectedCompany ? '0 2px 6px rgba(56, 139, 253, 0.2)' : 'none',
              transition: 'all 0.15s ease'
            }}
          >
            <Icons.ExternalLink size={13} />
            <span>Open Company</span>
          </button>

          {/* Add Company Action Button */}
          <button
            type="button"
            data-testid="add-company-btn"
            onClick={handleOpenAddModal}
            style={{
              backgroundColor: 'var(--primary-color)',
              color: 'var(--button-primary-text, #ffffff)',
              border: 'none',
              borderRadius: 'var(--radius-md, 6px)',
              padding: '8px 16px',
              fontSize: '12px',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.25)',
              transition: 'all 0.15s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
            onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
          >
            <Icons.Plus size={14} />
            <span>Add Company</span>
          </button>
        </div>
      </div>

      {/* Top Summary KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '12px',
        width: '100%'
      }}>
        {/* KPI 1: Companies */}
        <div style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--border-color)',
          borderRadius: '8px',
          padding: '14px 18px',
          boxShadow: 'var(--card-shadow)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <span style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-mute)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Companies</span>
            <Icons.Building size={16} style={{ color: 'var(--primary-color)' }} />
          </div>
          <div style={{ fontSize: '22px', fontWeight: '700', color: 'var(--text-main)' }}>
            {kpis.totalCompanies}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '4px' }}>
            Registered organizations
          </div>
        </div>

        {/* KPI 2: Operators */}
        <div style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--border-color)',
          borderRadius: '8px',
          padding: '14px 18px',
          boxShadow: 'var(--card-shadow)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <span style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-mute)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Assigned Operators</span>
            <Icons.Users size={16} style={{ color: '#a0c4ff' }} />
          </div>
          <div style={{ fontSize: '22px', fontWeight: '700', color: 'var(--text-main)' }}>
            {kpis.totalUsers}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '4px' }}>
            Active company members
          </div>
        </div>

        {/* KPI 3: Orders */}
        <div style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--border-color)',
          borderRadius: '8px',
          padding: '14px 18px',
          boxShadow: 'var(--card-shadow)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <span style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-mute)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Commercial Orders</span>
            <Icons.ShoppingBag size={16} style={{ color: '#34d399' }} />
          </div>
          <div style={{ fontSize: '22px', fontWeight: '700', color: 'var(--text-main)' }}>
            {kpis.totalOrders}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '4px' }}>
            Placed across companies
          </div>
        </div>

        {/* KPI 4: Total Spend */}
        <div style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--border-color)',
          borderRadius: '8px',
          padding: '14px 18px',
          boxShadow: 'var(--card-shadow)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <span style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-mute)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Spend Volume</span>
            <Icons.DollarSign size={16} style={{ color: '#fbbf24' }} />
          </div>
          <div style={{ fontSize: '22px', fontWeight: '700', color: 'var(--text-main)' }}>
            ${kpis.totalVolume.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '4px' }}>
            Commercial coin billing
          </div>
        </div>
      </div>

      {/* Companies List Table / Cards */}
      {loading ? (
        <div style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--border-color)',
          borderRadius: '8px',
          padding: '50px 20px',
          textAlign: 'center',
          color: 'var(--text-mute)'
        }}>
          <div style={{
            width: '28px',
            height: '28px',
            border: '3px solid rgba(160, 196, 255, 0.2)',
            borderTopColor: 'var(--primary-color)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 12px auto'
          }} />
          <div style={{ fontSize: '13px', color: 'var(--text-mute)' }}>Loading registered companies...</div>
        </div>
      ) : filteredCompanies.length === 0 ? (
        <div style={{
          background: 'var(--card-bg)',
          border: '1px dashed var(--border-color)',
          borderRadius: '8px',
          padding: '50px 20px',
          textAlign: 'center',
          color: 'var(--text-mute)'
        }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.4, margin: '0 auto 12px auto', display: 'block' }}>
            <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
            <path d="M9 22v-4h6v4" />
            <path d="M8 6h.01" /><path d="M16 6h.01" />
            <path d="M8 10h.01" /><path d="M16 10h.01" />
            <path d="M8 14h.01" /><path d="M16 14h.01" />
          </svg>
          <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-main)', marginBottom: '4px' }}>
            {searchQuery ? 'No matching companies found' : 'No companies registered yet'}
          </div>
          <div style={{ fontSize: '12px', maxWidth: '420px', margin: '0 auto' }}>
            {searchQuery 
              ? `We couldn't find any companies matching "${searchQuery}". Try adjusting your keywords or clearing the search.`
              : 'Add your first company or charter operator organization to begin managing operators, white-labeled branding, and orders.'}
          </div>
          {!searchQuery && (
            <button
              type="button"
              onClick={handleOpenAddModal}
              style={{
                marginTop: '16px',
                backgroundColor: 'var(--primary-color)',
                color: 'var(--button-primary-text, #ffffff)',
                border: 'none',
                borderRadius: 'var(--radius-md, 6px)',
                padding: '8px 16px',
                fontSize: '12px',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.25)'
              }}
            >
              + Add First Company
            </button>
          )}
        </div>
      ) : (
        <>
          <div style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--border-color)',
          borderRadius: '8px',
          overflowX: 'auto',
          boxShadow: 'var(--card-shadow)'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border-color)', backgroundColor: 'var(--table-header-bg, rgba(255, 255, 255, 0.02))' }}>
                <th style={{ padding: '12px 16px', color: 'var(--table-header-text, var(--text-mute))', fontWeight: '600', fontSize: '12px' }}>company</th>
                {isGlobalRole && <th style={{ padding: '12px 16px', color: 'var(--table-header-text, var(--text-mute))', fontWeight: '600', fontSize: '12px' }}>tenant domain</th>}
                <th style={{ padding: '12px 16px', color: 'var(--table-header-text, var(--text-mute))', fontWeight: '600', fontSize: '12px' }}>contact & links</th>
                <th style={{ padding: '12px 16px', color: 'var(--table-header-text, var(--text-mute))', fontWeight: '600', fontSize: '12px' }}>location</th>
                <th style={{ padding: '12px 16px', color: 'var(--table-header-text, var(--text-mute))', fontWeight: '600', fontSize: '12px' }}>assigned agent</th>
                <th style={{ padding: '12px 16px', color: 'var(--table-header-text, var(--text-mute))', fontWeight: '600', fontSize: '12px', textAlign: 'center' }}>operators</th>
                <th style={{ padding: '12px 16px', color: 'var(--table-header-text, var(--text-mute))', fontWeight: '600', fontSize: '12px', textAlign: 'center' }}>orders</th>
                <th style={{ padding: '12px 16px', color: 'var(--table-header-text, var(--text-mute))', fontWeight: '600', fontSize: '12px', textAlign: 'right' }}>total spend</th>
                <th style={{ padding: '12px 16px', color: 'var(--table-header-text, var(--text-mute))', fontWeight: '600', fontSize: '12px', textAlign: 'right' }}>actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.map((comp) => {
                const isSelected = selectedCompanyId === comp.id;
                return (
                  <tr
                    key={comp.id}
                    data-testid={`company-row-${comp.id}`}
                    tabIndex={0}
                    onClick={() => setSelectedCompanyId(comp.id)}
                    onDoubleClick={() => handleOpenEditModal(comp, 'details')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleOpenEditModal(comp, 'details');
                      }
                    }}
                    style={{
                      borderBottom: '1px solid var(--border-color)',
                      backgroundColor: isSelected ? 'var(--table-row-selected, rgba(56, 139, 253, 0.12))' : 'transparent',
                      borderLeft: isSelected ? '3px solid var(--primary-color, #38bdf8)' : '3px solid transparent',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.03)';
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    {/* 1. Company Name & Logo */}
                    <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '38px',
                          height: '38px',
                          borderRadius: '6px',
                          backgroundColor: 'rgba(255, 255, 255, 0.04)',
                          border: '1px solid var(--border-color)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          flexShrink: 0
                        }}>
                          {comp.logo_url ? (
                            <img src={comp.logo_url} alt={comp.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                          ) : (
                            <span style={{ fontSize: '16px', fontWeight: 700, color: 'var(--primary-color)' }}>
                              {comp.name ? comp.name.charAt(0).toUpperCase() : '🏢'}
                            </span>
                          )}
                        </div>
                        <div>
                          <div style={{ fontWeight: '700', color: 'var(--text-main)', fontSize: '13px' }}>
                            {comp.name}
                          </div>
                          {comp.created_at && (
                            <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '2px' }}>
                              Registered {new Date(comp.created_at).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* 2. Tenant Brand (Global Admins Only) */}
                    {isGlobalRole && (
                      <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          backgroundColor: 'rgba(160, 196, 255, 0.12)',
                          border: '1px solid rgba(160, 196, 255, 0.25)',
                          color: '#a0c4ff',
                          padding: '3px 8px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          <Icons.Globe size={11} />
                          {comp.tenant_name || `Tenant #${comp.tenant_id}`}
                        </span>
                      </td>
                    )}

                    {/* 3. Contact & Links */}
                    <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                        {comp.enquiries_email ? (
                          <a
                            href={`mailto:${comp.enquiries_email}`}
                            onClick={(e) => e.stopPropagation()}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: 'var(--text-main)', textDecoration: 'none', fontSize: '12px' }}
                          >
                            <Icons.Mail size={12} style={{ color: 'var(--primary-color)' }} />
                            <span>{comp.enquiries_email}</span>
                          </a>
                        ) : (
                          <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>No email</span>
                        )}
                        {comp.website_address && (
                          <a
                            href={comp.website_address.startsWith('http') ? comp.website_address : `https://${comp.website_address}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: '#a0c4ff', textDecoration: 'none', fontSize: '11px' }}
                          >
                            <Icons.ExternalLink size={11} />
                            <span>Website</span>
                          </a>
                        )}
                      </div>
                    </td>

                    {/* 4. Location */}
                    <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
                      {comp.billing_city || comp.billing_country ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'var(--text-main)', fontSize: '12px' }}>
                          <Icons.MapPin size={12} style={{ color: '#fbbf24', flexShrink: 0 }} />
                          <span>
                            {[comp.billing_city, comp.billing_state, comp.billing_country].filter(Boolean).join(', ')}
                          </span>
                        </div>
                      ) : (
                        <span style={{ fontSize: '12px', color: 'var(--text-mute)' }}>—</span>
                      )}
                    </td>

                    {/* Assigned Agent */}
                    <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
                      {comp.active_agent ? (
                        <div>
                          <div style={{ fontWeight: '600', color: 'var(--text-main)', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10b981', flexShrink: 0 }} />
                            <span>{comp.active_agent.name}</span>
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '2px' }}>
                            {comp.contract_info ? `${comp.contract_info.remaining_days}d left (${comp.contract_info.commission_rate_pct}%)` : comp.active_agent.email}
                          </div>
                        </div>
                      ) : (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          color: 'var(--text-mute)',
                          border: '1px dashed var(--border-color)'
                        }}>
                          Pool (Unassigned)
                        </span>
                      )}
                    </td>

                    {/* 5. Operators Count (Clickable) */}
                    <td style={{ padding: '12px 16px', textAlign: 'center', verticalAlign: 'middle' }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenUsersModal(comp);
                        }}
                        title="Manage Company Operators"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          backgroundColor: 'rgba(168, 85, 247, 0.12)',
                          border: '1px solid rgba(168, 85, 247, 0.3)',
                          color: '#c084fc',
                          borderRadius: '6px',
                          padding: '4px 10px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'background-color 0.15s ease'
                        }}
                      >
                        <Icons.Users size={12} />
                        <span>{comp.users_count || 0}</span>
                      </button>
                    </td>

                    {/* 6. Orders Count (Clickable) */}
                    <td style={{ padding: '12px 16px', textAlign: 'center', verticalAlign: 'middle' }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenOrdersModal(comp);
                        }}
                        title="View Company Orders"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          backgroundColor: 'rgba(16, 185, 129, 0.12)',
                          border: '1px solid rgba(16, 185, 129, 0.3)',
                          color: '#34d399',
                          borderRadius: '6px',
                          padding: '4px 10px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'background-color 0.15s ease'
                        }}
                      >
                        <Icons.ShoppingBag size={12} />
                        <span>{comp.orders_count || 0}</span>
                      </button>
                    </td>

                    {/* 7. Total Spend */}
                    <td style={{ padding: '12px 16px', textAlign: 'right', verticalAlign: 'middle' }}>
                      <div style={{ fontWeight: '700', color: 'var(--text-main)', fontSize: '13px' }}>
                        ${(comp.total_spent || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '2px' }}>
                        {comp.coins_count || 0} coins
                      </div>
                    </td>

                    {/* 8. Actions */}
                    <td style={{ padding: '12px 16px', textAlign: 'right', verticalAlign: 'middle' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        {/* Users Modal Button */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenUsersModal(comp);
                          }}
                          title="Assign or remove operators"
                          style={{
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--border-color)',
                            color: 'var(--text-mute)',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseOver={(e) => { e.currentTarget.style.color = 'var(--text-main)'; e.currentTarget.style.borderColor = 'var(--primary-color)'; }}
                          onMouseOut={(e) => { e.currentTarget.style.color = 'var(--text-mute)'; e.currentTarget.style.borderColor = 'var(--border-color)'; }}
                        >
                          <Icons.Users size={14} />
                        </button>

                        {/* Orders Modal Button */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenOrdersModal(comp);
                          }}
                          title="View order history"
                          style={{
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--border-color)',
                            color: 'var(--text-mute)',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseOver={(e) => { e.currentTarget.style.color = 'var(--text-main)'; e.currentTarget.style.borderColor = 'var(--primary-color)'; }}
                          onMouseOut={(e) => { e.currentTarget.style.color = 'var(--text-mute)'; e.currentTarget.style.borderColor = 'var(--border-color)'; }}
                        >
                          <Icons.ShoppingBag size={14} />
                        </button>

                        {/* Edit Button */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEditModal(comp, 'details');
                          }}
                          title="Edit details"
                          style={{
                            backgroundColor: 'rgba(56, 139, 253, 0.1)',
                            border: '1px solid rgba(56, 139, 253, 0.3)',
                            color: '#58a6ff',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(56, 139, 253, 0.2)'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(56, 139, 253, 0.1)'}
                        >
                          <Icons.Edit size={14} />
                        </button>

                        {/* Delete Button */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeletingCompany(comp);
                          }}
                          title="Delete company"
                          style={{
                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                            border: '1px solid rgba(239, 68, 68, 0.25)',
                            color: '#ff7b72',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.2)'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)'}
                        >
                          <Icons.Trash size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredCompanies.length > 0 && (
          <PaginationBar
            currentPage={currentPage}
            totalPages={totalPages || Math.max(1, Math.ceil(totalCompanies / pageSize))}
            pageSize={pageSize}
            totalItems={totalCompanies || filteredCompanies.length}
            onPageChange={(newPage) => setCurrentPage(newPage)}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setCurrentPage(1);
            }}
            pageSizeOptions={[25, 50, 100]}
            itemName="companies"
          />
        )}
      </>
    )}

      {/* ========================================================================= */}
      {/* UNIFIED COMPANY DETAILS, ADDRESS, USERS & ORDERS POPUP PANEL             */}
      {/* ========================================================================= */}
      {editingCompany && createPortal(
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setEditingCompany(null);
            }
          }}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(5, 8, 16, 0.85)',
            backdropFilter: 'blur(12px)',
            zIndex: 999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="company-popup-title"
            style={{
              background: 'var(--modal-bg, var(--card-bg, #111622))',
              border: '1px solid var(--modal-border, var(--border-color))',
              borderRadius: 'var(--radius-lg, 12px)',
              width: '100%',
              maxWidth: '840px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.6)',
              overflow: 'hidden'
            }}
          >
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px 12px 20px',
              borderBottom: '1px solid var(--border-color)',
              backgroundColor: 'var(--table-header-bg, rgba(255, 255, 255, 0.02))'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '38px',
                  height: '38px',
                  borderRadius: '6px',
                  backgroundColor: 'rgba(56, 139, 253, 0.1)',
                  border: '1px solid rgba(56, 139, 253, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  flexShrink: 0
                }}>
                  {formData.logo_url ? (
                    <img src={formData.logo_url} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : (
                    <Icons.Building size={20} style={{ color: 'var(--primary-color)' }} />
                  )}
                </div>
                <div>
                  <h3 id="company-popup-title" style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {isNewCompany ? 'Add New Company' : (editingCompany.name || 'Company Details')}
                  </h3>
                  {!isNewCompany && editingCompany && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px', fontSize: '11px', color: 'var(--text-mute)' }}>
                      <span>ID: #{editingCompany.id}</span>
                      {editingCompany.billing_city && <span>• {editingCompany.billing_city}</span>}
                      {editingCompany.created_at && (
                        <span>• Registered {new Date(editingCompany.created_at).toLocaleDateString()}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <button
                type="button"
                data-testid="company-modal-close-btn"
                onClick={() => {
                  setEditingCompany(null);
                }}
                style={{ background: 'none', border: 'none', color: 'var(--text-mute)', cursor: 'pointer', padding: '6px', borderRadius: '4px' }}
                title="Close"
              >
                <Icons.Close size={18} />
              </button>
            </div>

            {/* TAB NAVIGATION BAR */}
            <div
              data-testid="company-modal-tabs"
              style={{
                display: 'flex',
                gap: '4px',
                padding: '0 16px',
                borderBottom: '1px solid var(--border-color)',
                backgroundColor: 'rgba(0, 0, 0, 0.15)',
                overflowX: 'auto'
              }}
            >
              {/* Tab 1: Details */}
              <button
                type="button"
                data-testid="tab-company-details"
                onClick={() => setActiveCompanyTab('details')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  borderBottom: activeCompanyTab === 'details' ? '2px solid var(--primary-color)' : '2px solid transparent',
                  color: activeCompanyTab === 'details' ? 'var(--primary-color)' : 'var(--text-mute)',
                  fontWeight: activeCompanyTab === 'details' ? 700 : 500,
                  fontSize: '13px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  whiteSpace: 'nowrap'
                }}
              >
                <Icons.Building size={14} />
                <span>Details</span>
              </button>

              {/* Tab 2: Address Info */}
              <button
                type="button"
                data-testid="tab-company-address"
                onClick={() => setActiveCompanyTab('address')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  borderBottom: activeCompanyTab === 'address' ? '2px solid var(--primary-color)' : '2px solid transparent',
                  color: activeCompanyTab === 'address' ? 'var(--primary-color)' : 'var(--text-mute)',
                  fontWeight: activeCompanyTab === 'address' ? 700 : 500,
                  fontSize: '13px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  whiteSpace: 'nowrap'
                }}
              >
                <Icons.MapPin size={14} />
                <span>Address Info</span>
                {companyDeliveryAddresses.length > 0 && (
                  <span style={{
                    fontSize: '10px',
                    padding: '1px 6px',
                    borderRadius: '10px',
                    backgroundColor: 'rgba(56, 189, 248, 0.15)',
                    color: '#38bdf8'
                  }}>
                    {companyDeliveryAddresses.length}
                  </span>
                )}
              </button>

              {/* Tab 3: Users */}
              <button
                type="button"
                data-testid="tab-company-users"
                disabled={isNewCompany}
                onClick={() => !isNewCompany && setActiveCompanyTab('users')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  borderBottom: activeCompanyTab === 'users' ? '2px solid var(--primary-color)' : '2px solid transparent',
                  color: activeCompanyTab === 'users' ? 'var(--primary-color)' : 'var(--text-mute)',
                  fontWeight: activeCompanyTab === 'users' ? 700 : 500,
                  fontSize: '13px',
                  cursor: isNewCompany ? 'not-allowed' : 'pointer',
                  opacity: isNewCompany ? 0.45 : 1,
                  transition: 'all 0.15s ease',
                  whiteSpace: 'nowrap'
                }}
                title={isNewCompany ? 'Save company first to manage operators' : 'Manage Company Operators'}
              >
                <Icons.Users size={14} />
                <span>Users & Operators</span>
                {!isNewCompany && (
                  <span style={{
                    fontSize: '10px',
                    padding: '1px 6px',
                    borderRadius: '10px',
                    backgroundColor: 'var(--badge-bg, rgba(168, 85, 247, 0.15))',
                    color: 'var(--badge-text, #c084fc)'
                  }}>
                    {companyUsers.length}
                  </span>
                )}
              </button>

              {/* Tab 4: Account (Orders) */}
              <button
                type="button"
                data-testid="tab-company-account"
                disabled={isNewCompany}
                onClick={() => !isNewCompany && setActiveCompanyTab('account')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  borderBottom: activeCompanyTab === 'account' ? '2px solid var(--primary-color)' : '2px solid transparent',
                  color: activeCompanyTab === 'account' ? 'var(--primary-color)' : 'var(--text-mute)',
                  fontWeight: activeCompanyTab === 'account' ? 700 : 500,
                  fontSize: '13px',
                  cursor: isNewCompany ? 'not-allowed' : 'pointer',
                  opacity: isNewCompany ? 0.45 : 1,
                  transition: 'all 0.15s ease',
                  whiteSpace: 'nowrap'
                }}
                title={isNewCompany ? 'Save company first to view order history' : 'Account & Orders History'}
              >
                <Icons.ShoppingBag size={14} />
                <span>Account & Orders</span>
                {!isNewCompany && (
                  <span style={{
                    fontSize: '10px',
                    padding: '1px 6px',
                    borderRadius: '10px',
                    backgroundColor: 'var(--badge-bg, rgba(16, 185, 129, 0.15))',
                    color: 'var(--badge-text, #34d399)'
                  }}>
                    {companyOrders.length}
                  </span>
                )}
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ display: 'flex', flexDirection: 'column', overflowY: 'auto', flex: 1, padding: '20px', gap: '16px' }}>
              {/* TAB 1: DETAILS */}
              {activeCompanyTab === 'details' && (
                <>
                  {companyModalError && (
                    <div style={{
                      backgroundColor: 'rgba(239, 68, 68, 0.15)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      color: '#f87171',
                      borderRadius: '6px',
                      padding: '10px 14px',
                      fontSize: '13px'
                    }}>
                      {companyModalError}
                    </div>
                  )}

              {/* Dedicated Agent & Contract Card */}
              {!isNewCompany && editingCompany && (
                <div style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.02)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  padding: '14px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>🤝</span>
                      <span>Dedicated Agent Partner & Contract</span>
                    </div>
                    {editingCompany.contract_info && (
                      <span style={{
                        padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700',
                        backgroundColor: 'rgba(16, 185, 129, 0.15)', color: '#10b981'
                      }}>
                        {editingCompany.contract_info.remaining_days} Days Remaining
                      </span>
                    )}
                  </div>

                  {editingCompany.active_agent ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                      <div>
                        <div style={{ fontWeight: '600', color: 'var(--text-main)', fontSize: '13px' }}>
                          {editingCompany.active_agent.name}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-mute)' }}>
                          {editingCompany.active_agent.email} {editingCompany.active_agent.phone && `• ${editingCompany.active_agent.phone}`}
                        </div>
                        <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                          Contract Terms: {editingCompany.contract_info?.commission_rate_pct}% Commission &bull; Expires {editingCompany.contract_info?.end_date ? new Date(editingCompany.contract_info.end_date).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={async () => {
                          const reason = await modal.prompt('Void Agent Contract', 'State the Quality of Service (QoS) failure reason to void this contract and release the operator to the pool:');
                          if (!reason) return;
                          try {
                            const res = await fetch(`${API_BASE}/api/v1/agent-network/contracts/${editingCompany.contract_info.id}/void`, {
                              method: 'POST',
                              headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json',
                                'X-Tenant-Domain': window.location.hostname
                              },
                              body: JSON.stringify({ void_reason: reason })
                            });
                            const data = await res.json();
                            if (!res.ok) throw new Error(data.detail || 'Could not void contract.');
                            modal.alert('Contract Voided', data.message || 'Operator released to unassigned pool.');
                            setEditingCompany(null);
                            fetchCompanies();
                          } catch (err) {
                            modal.alert('Error', err.message);
                          }
                        }}
                        style={{
                          padding: '6px 12px', borderRadius: '6px',
                          backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)',
                          color: '#ef4444', fontSize: '11px', fontWeight: '600', cursor: 'pointer'
                        }}
                      >
                        Void Contract (Release to Pool)
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: '12px', color: 'var(--text-mute)' }}>
                        This operator currently has no assigned agent partner (in Unassigned Pool).
                      </div>
                      <span style={{
                        padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600',
                        backgroundColor: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b'
                      }}>
                        Unallocated Pool
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Logo Management Row */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                padding: '14px',
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '8px'
              }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '8px',
                  backgroundColor: '#1e293b',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  flexShrink: 0
                }}>
                  {formData.logo_url ? (
                    <img src={formData.logo_url} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : (
                    <span style={{ fontSize: '24px', fontWeight: 'bold', color: '#00c6ff' }}>
                      {formData.name ? formData.name.charAt(0).toUpperCase() : '🏢'}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#ffffff' }}>Company Logo</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label style={{
                      backgroundColor: 'var(--primary-color)',
                      color: '#ffffff',
                      borderRadius: '6px',
                      padding: '5px 12px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}>
                      {logoUploading ? 'Uploading...' : 'Upload Image'}
                      <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} disabled={logoUploading} />
                    </label>
                    {formData.logo_url && (
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, logo_url: '' }))}
                        style={{
                          backgroundColor: 'transparent',
                          color: '#ef4444',
                          border: '1px solid rgba(239, 68, 68, 0.3)',
                          borderRadius: '6px',
                          padding: '5px 10px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Remove Logo
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Company Name & Tenant Assignment */}
              <div style={{ display: 'grid', gridTemplateColumns: isGlobalRole ? '2fr 1fr' : '1fr', gap: '14px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#cbd5e1' }}>
                    Company / Organisation Name <span style={{ color: '#f87171' }}>*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Key West Deep Sea Charters"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    style={{
                      backgroundColor: '#1e293b',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      color: '#ffffff',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      fontSize: '13px',
                      outline: 'none'
                    }}
                  />
                </div>

                {isGlobalRole && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '12px', fontWeight: 600, color: '#cbd5e1' }}>Tenant Domain</label>
                    <select
                      value={formData.tenant_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, tenant_id: e.target.value }))}
                      style={{
                        backgroundColor: '#1e293b',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        color: '#ffffff',
                        borderRadius: '6px',
                        padding: '8px 10px',
                        fontSize: '13px',
                        outline: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">Default / Unassigned</option>
                      {tenants.map(t => (
                        <option key={t.id} value={t.id.toString()}>{t.brand_name || t.domain_name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Contact Email & Website */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#cbd5e1' }}>Enquiries Email</label>
                  <input
                    type="email"
                    placeholder="contact@company.com"
                    value={formData.enquiries_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, enquiries_email: e.target.value }))}
                    style={{
                      backgroundColor: '#1e293b',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      color: '#ffffff',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      fontSize: '13px',
                      outline: 'none'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#cbd5e1' }}>Website Address</label>
                  <input
                    type="text"
                    placeholder="https://company.com"
                    value={formData.website_address}
                    onChange={(e) => setFormData(prev => ({ ...prev, website_address: e.target.value }))}
                    style={{
                      backgroundColor: '#1e293b',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      color: '#ffffff',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      fontSize: '13px',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>

              {/* Bookings Link & Social Media */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#cbd5e1' }}>Online Bookings URL</label>
                  <input
                    type="text"
                    placeholder="https://book.company.com"
                    value={formData.bookings_link}
                    onChange={(e) => setFormData(prev => ({ ...prev, bookings_link: e.target.value }))}
                    style={{
                      backgroundColor: '#1e293b',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      color: '#ffffff',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      fontSize: '13px',
                      outline: 'none'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#cbd5e1' }}>Social Media Links</label>
                  <input
                    type="text"
                    placeholder="@instagram or facebook link"
                    value={formData.social_media}
                    onChange={(e) => setFormData(prev => ({ ...prev, social_media: e.target.value }))}
                    style={{
                      backgroundColor: '#1e293b',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      color: '#ffffff',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      fontSize: '13px',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>
            </>
          )}

          {/* TAB 2: ADDRESS INFO */}
          {activeCompanyTab === 'address' && (
            <>
              {companyModalError && (
                <div style={{
                  backgroundColor: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#f87171',
                  borderRadius: '6px',
                  padding: '10px 14px',
                  fontSize: '13px'
                }}>
                  {companyModalError}
                </div>
              )}

              {/* Billing Address Sub-Section */}
              <div style={{
                marginTop: '6px',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                paddingTop: '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#ffffff' }}>Billing Address</span>

                {/* Smart Fill-in for Billing Address */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '11px', color: '#38bdf8', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span>⚡ Smart Fill-in Billing Address</span>
                    <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 'normal' }}>(type street, city or business to auto-complete)</span>
                  </label>
                  <AddressAutocompleteInput
                    placeholder="Search street, city or business (e.g. 120 Albert St Auckland)..."
                    onSelectAddress={(s) => {
                      setFormData(prev => ({
                        ...prev,
                        billing_address: s.street || s.formatted_address.split(',')[0],
                        billing_city: s.city,
                        billing_state: s.state,
                        billing_zip: s.zip,
                        billing_country: s.country
                      }));
                    }}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '12px', color: '#cbd5e1' }}>Street Address</label>
                  <input
                    type="text"
                    placeholder="123 Ocean Blvd"
                    value={formData.billing_address}
                    onChange={(e) => setFormData(prev => ({ ...prev, billing_address: e.target.value }))}
                    style={{
                      backgroundColor: '#1e293b',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      color: '#ffffff',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      fontSize: '13px',
                      outline: 'none'
                    }}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '10px' }}>
                  <div>
                    <label style={{ fontSize: '11px', color: '#cbd5e1' }}>City</label>
                    <input
                      type="text"
                      placeholder="City"
                      value={formData.billing_city}
                      onChange={(e) => setFormData(prev => ({ ...prev, billing_city: e.target.value }))}
                      style={{
                        backgroundColor: '#1e293b',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        color: '#ffffff',
                        borderRadius: '6px',
                        padding: '6px 10px',
                        fontSize: '12px',
                        width: '100%',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', color: '#cbd5e1' }}>State / Region</label>
                    <input
                      type="text"
                      placeholder="State"
                      value={formData.billing_state}
                      onChange={(e) => setFormData(prev => ({ ...prev, billing_state: e.target.value }))}
                      style={{
                        backgroundColor: '#1e293b',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        color: '#ffffff',
                        borderRadius: '6px',
                        padding: '6px 10px',
                        fontSize: '12px',
                        width: '100%',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', color: '#cbd5e1' }}>Postal / ZIP</label>
                    <input
                      type="text"
                      placeholder="ZIP"
                      value={formData.billing_zip}
                      onChange={(e) => setFormData(prev => ({ ...prev, billing_zip: e.target.value }))}
                      style={{
                        backgroundColor: '#1e293b',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        color: '#ffffff',
                        borderRadius: '6px',
                        padding: '6px 10px',
                        fontSize: '12px',
                        width: '100%',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', color: '#cbd5e1' }}>Country</label>
                    <input
                      type="text"
                      placeholder="Country"
                      value={formData.billing_country}
                      onChange={(e) => setFormData(prev => ({ ...prev, billing_country: e.target.value }))}
                      style={{
                        backgroundColor: '#1e293b',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        color: '#ffffff',
                        borderRadius: '6px',
                        padding: '6px 10px',
                        fontSize: '12px',
                        width: '100%',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Delivery Addresses Section (Multiple Locations & Courier Integration) */}
              <div style={{
                marginTop: '6px',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                paddingTop: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icons.Truck size={18} style={{ color: '#38bdf8' }} />
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: '#ffffff' }}>Delivery Addresses</div>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                        Multiple delivery locations for coin shipments, courier API rate lookups, and dispatch
                      </div>
                    </div>
                  </div>
                  {!showAddDeliveryForm && (
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddDeliveryForm(true);
                        setDeliveryError('');
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        backgroundColor: 'rgba(56, 189, 248, 0.15)',
                        color: '#38bdf8',
                        border: '1px solid rgba(56, 189, 248, 0.3)',
                        borderRadius: '6px',
                        padding: '5px 12px',
                        fontSize: '11px',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      <Icons.Plus size={13} />
                      Add Delivery Address
                    </button>
                  )}
                </div>

                {/* Existing Delivery Addresses List */}
                {deliveryAddressesLoading ? (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#94a3b8', fontSize: '12px' }}>
                    Loading delivery addresses...
                  </div>
                ) : companyDeliveryAddresses.length === 0 && !showAddDeliveryForm ? (
                  <div style={{
                    padding: '16px',
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    border: '1px dashed rgba(255, 255, 255, 0.15)',
                    borderRadius: '8px',
                    textAlign: 'center',
                    color: '#94a3b8',
                    fontSize: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span>No separate delivery addresses registered yet. (Shipments will default to the billing address).</span>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddDeliveryForm(true);
                        setDeliveryError('');
                      }}
                      style={{
                        backgroundColor: 'transparent',
                        border: '1px solid rgba(56, 189, 248, 0.4)',
                        color: '#38bdf8',
                        borderRadius: '6px',
                        padding: '4px 12px',
                        fontSize: '11px',
                        cursor: 'pointer'
                      }}
                    >
                      + Add Delivery Address
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {companyDeliveryAddresses.map((addr, idx) => (
                      <div
                        key={addr.id || idx}
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.03)',
                          border: addr.is_default ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          padding: '12px 14px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontWeight: 700, fontSize: '12px', color: '#f8fafc' }}>
                              {addr.label || `Location #${idx + 1}`}
                            </span>
                            {addr.is_default ? (
                              <span style={{
                                backgroundColor: 'rgba(16, 185, 129, 0.2)',
                                color: '#34d399',
                                border: '1px solid rgba(16, 185, 129, 0.3)',
                                padding: '1px 6px',
                                borderRadius: '10px',
                                fontSize: '10px',
                                fontWeight: 700
                              }}>
                                Default Delivery
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleSetDefaultCompanyAddress(addr.id)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#38bdf8',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  padding: 0,
                                  textDecoration: 'underline'
                                }}
                              >
                                Set as Default
                              </button>
                            )}
                          </div>

                          <button
                            type="button"
                            onClick={() => handleDeleteCompanyAddress(addr.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#ef4444',
                              cursor: 'pointer',
                              fontSize: '11px',
                              padding: '2px 6px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            title="Delete address"
                          >
                            × Remove
                          </button>
                        </div>

                        <div style={{ fontSize: '12px', color: '#cbd5e1', lineHeight: 1.4 }}>
                          <div>
                            {addr.street}
                            {addr.street2 ? ` (${addr.street2})` : ''}
                          </div>
                          <div style={{ color: '#94a3b8', fontSize: '11px' }}>
                            {[addr.city, addr.state, addr.zip, addr.country].filter(Boolean).join(', ')}
                            {addr.country_code ? ` [${addr.country_code}]` : ''}
                          </div>
                        </div>

                        {(addr.recipient_name || addr.phone || (addr.latitude && addr.longitude) || addr.delivery_instructions) && (
                          <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '8px',
                            alignItems: 'center',
                            fontSize: '11px',
                            color: '#94a3b8',
                            marginTop: '4px',
                            paddingTop: '6px',
                            borderTop: '1px solid rgba(255, 255, 255, 0.05)'
                          }}>
                            {addr.recipient_name && <span>👤 Contact: <strong style={{ color: '#e2e8f0' }}>{addr.recipient_name}</strong></span>}
                            {addr.phone && <span>📞 Tel: <strong style={{ color: '#e2e8f0' }}>{addr.phone}</strong></span>}
                            {addr.latitude && addr.longitude && (
                              <span style={{ backgroundColor: 'rgba(56, 189, 248, 0.1)', color: '#38bdf8', padding: '1px 5px', borderRadius: '4px' }}>
                                📍 Geocoded: {addr.latitude}, {addr.longitude}
                              </span>
                            )}
                            {addr.delivery_instructions && (
                              <span style={{ fontStyle: 'italic', color: '#cbd5e1' }}>
                                Notes: "{addr.delivery_instructions}"
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Delivery Address Form */}
                {showAddDeliveryForm && (
                  <div style={{
                    backgroundColor: '#0b1120',
                    border: '1px solid rgba(56, 189, 248, 0.35)',
                    borderRadius: '8px',
                    padding: '14px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#38bdf8' }}>
                        New Delivery Location
                      </span>
                      <button
                        type="button"
                        onClick={handleCopyBillingToDelivery}
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.08)',
                          border: '1px solid rgba(255, 255, 255, 0.15)',
                          color: '#cbd5e1',
                          borderRadius: '5px',
                          padding: '4px 8px',
                          fontSize: '11px',
                          cursor: 'pointer'
                        }}
                        title="Pre-populate with current billing address values"
                      >
                        📋 Copy from Billing Address
                      </button>
                    </div>

                    {deliveryError && (
                      <div style={{
                        backgroundColor: 'rgba(239, 68, 68, 0.15)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        color: '#f87171',
                        borderRadius: '5px',
                        padding: '6px 10px',
                        fontSize: '11px'
                      }}>
                        {deliveryError}
                      </div>
                    )}

                    {/* Smart Fill-In Address Search */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '11px', color: '#38bdf8', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span>⚡ Smart Fill-in Address (Courier Verified)</span>
                        <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 'normal' }}>(type street, city or business to auto-complete)</span>
                      </label>
                      <AddressAutocompleteInput
                        placeholder="Start typing to smart fill delivery address..."
                        onSelectAddress={(s) => {
                          setDeliveryForm(prev => ({
                            ...prev,
                            street: s.street || s.formatted_address.split(',')[0],
                            city: s.city,
                            state: s.state,
                            zip: s.zip,
                            country: s.country,
                            country_code: s.country_code,
                            latitude: s.latitude,
                            longitude: s.longitude
                          }));
                        }}
                      />
                    </div>

                    {/* Label & Recipient Contact */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                      <div>
                        <label style={{ fontSize: '11px', color: '#cbd5e1' }}>Location Label</label>
                        <input
                          type="text"
                          placeholder="e.g. Main Warehouse"
                          value={deliveryForm.label}
                          onChange={(e) => setDeliveryForm(prev => ({ ...prev, label: e.target.value }))}
                          style={{
                            backgroundColor: '#1e293b',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                            color: '#ffffff',
                            borderRadius: '6px',
                            padding: '6px 10px',
                            fontSize: '12px',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '11px', color: '#cbd5e1' }}>Attention / Recipient Name</label>
                        <input
                          type="text"
                          placeholder="e.g. Receiving Manager"
                          value={deliveryForm.recipient_name}
                          onChange={(e) => setDeliveryForm(prev => ({ ...prev, recipient_name: e.target.value }))}
                          style={{
                            backgroundColor: '#1e293b',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                            color: '#ffffff',
                            borderRadius: '6px',
                            padding: '6px 10px',
                            fontSize: '12px',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '11px', color: '#cbd5e1' }}>
                          Contact Phone <span style={{ color: '#38bdf8', fontSize: '10px' }}>(courier required)</span>
                        </label>
                        <input
                          type="text"
                          placeholder="+1 555-0199"
                          value={deliveryForm.phone}
                          onChange={(e) => setDeliveryForm(prev => ({ ...prev, phone: e.target.value }))}
                          style={{
                            backgroundColor: '#1e293b',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                            color: '#ffffff',
                            borderRadius: '6px',
                            padding: '6px 10px',
                            fontSize: '12px',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                    </div>

                    {/* Street Address Line 1 & Line 2 */}
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px' }}>
                      <div>
                        <label style={{ fontSize: '11px', color: '#cbd5e1' }}>Street Address *</label>
                        <input
                          type="text"
                          required
                          placeholder="123 Ocean Blvd"
                          value={deliveryForm.street}
                          onChange={(e) => setDeliveryForm(prev => ({ ...prev, street: e.target.value }))}
                          style={{
                            backgroundColor: '#1e293b',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                            color: '#ffffff',
                            borderRadius: '6px',
                            padding: '6px 10px',
                            fontSize: '12px',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '11px', color: '#cbd5e1' }}>Apt / Suite / Unit (Line 2)</label>
                        <input
                          type="text"
                          placeholder="Suite 200 / Dock B"
                          value={deliveryForm.street2}
                          onChange={(e) => setDeliveryForm(prev => ({ ...prev, street2: e.target.value }))}
                          style={{
                            backgroundColor: '#1e293b',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                            color: '#ffffff',
                            borderRadius: '6px',
                            padding: '6px 10px',
                            fontSize: '12px',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                    </div>

                    {/* City, State, ZIP, Country, Country Code */}
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 60px', gap: '10px' }}>
                      <div>
                        <label style={{ fontSize: '11px', color: '#cbd5e1' }}>City / Suburb *</label>
                        <input
                          type="text"
                          required
                          placeholder="City"
                          value={deliveryForm.city}
                          onChange={(e) => setDeliveryForm(prev => ({ ...prev, city: e.target.value }))}
                          style={{
                            backgroundColor: '#1e293b',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                            color: '#ffffff',
                            borderRadius: '6px',
                            padding: '6px 10px',
                            fontSize: '12px',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '11px', color: '#cbd5e1' }}>State / Region</label>
                        <input
                          type="text"
                          placeholder="State"
                          value={deliveryForm.state}
                          onChange={(e) => setDeliveryForm(prev => ({ ...prev, state: e.target.value }))}
                          style={{
                            backgroundColor: '#1e293b',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                            color: '#ffffff',
                            borderRadius: '6px',
                            padding: '6px 10px',
                            fontSize: '12px',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '11px', color: '#cbd5e1' }}>Postal / ZIP *</label>
                        <input
                          type="text"
                          required
                          placeholder="ZIP"
                          value={deliveryForm.zip}
                          onChange={(e) => setDeliveryForm(prev => ({ ...prev, zip: e.target.value }))}
                          style={{
                            backgroundColor: '#1e293b',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                            color: '#ffffff',
                            borderRadius: '6px',
                            padding: '6px 10px',
                            fontSize: '12px',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '11px', color: '#cbd5e1' }}>Country *</label>
                        <input
                          type="text"
                          required
                          placeholder="Country"
                          value={deliveryForm.country}
                          onChange={(e) => setDeliveryForm(prev => ({ ...prev, country: e.target.value }))}
                          style={{
                            backgroundColor: '#1e293b',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                            color: '#ffffff',
                            borderRadius: '6px',
                            padding: '6px 10px',
                            fontSize: '12px',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '11px', color: '#cbd5e1' }}>ISO</label>
                        <input
                          type="text"
                          maxLength={3}
                          placeholder="NZ"
                          value={deliveryForm.country_code}
                          onChange={(e) => setDeliveryForm(prev => ({ ...prev, country_code: e.target.value.toUpperCase() }))}
                          style={{
                            backgroundColor: '#1e293b',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                            color: '#ffffff',
                            borderRadius: '6px',
                            padding: '6px 6px',
                            fontSize: '12px',
                            width: '100%',
                            textAlign: 'center',
                            boxSizing: 'border-box'
                          }}
                          title="2-letter ISO Country Code for Courier APIs (e.g. US, NZ, AU, GB)"
                        />
                      </div>
                    </div>

                    {/* Special Delivery Instructions */}
                    <div>
                      <label style={{ fontSize: '11px', color: '#cbd5e1' }}>Delivery Instructions / Access Notes</label>
                      <input
                        type="text"
                        placeholder="e.g. Loading dock at rear, gate code 1234, deliver between 8am-4pm"
                        value={deliveryForm.delivery_instructions}
                        onChange={(e) => setDeliveryForm(prev => ({ ...prev, delivery_instructions: e.target.value }))}
                        style={{
                          backgroundColor: '#1e293b',
                          border: '1px solid rgba(255, 255, 255, 0.15)',
                          color: '#ffffff',
                          borderRadius: '6px',
                          padding: '6px 10px',
                          fontSize: '12px',
                          width: '100%',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>

                    {/* Default Checkbox */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        id="delivery_default_check"
                        checked={deliveryForm.is_default}
                        onChange={(e) => setDeliveryForm(prev => ({ ...prev, is_default: e.target.checked }))}
                      />
                      <label htmlFor="delivery_default_check" style={{ fontSize: '12px', color: '#cbd5e1', cursor: 'pointer' }}>
                        Set as default delivery address for this company
                      </label>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '4px' }}>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddDeliveryForm(false);
                          setDeliveryError('');
                        }}
                        style={{
                          backgroundColor: 'transparent',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          color: '#cbd5e1',
                          borderRadius: '5px',
                          padding: '6px 12px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveDeliveryAddress}
                        disabled={deliverySaving}
                        style={{
                          backgroundColor: '#38bdf8',
                          border: 'none',
                          color: '#0f172a',
                          borderRadius: '5px',
                          padding: '6px 16px',
                          fontSize: '12px',
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                      >
                        {deliverySaving ? 'Saving...' : 'Save Delivery Address'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* TAB 3: USERS & OPERATORS */}
          {activeCompanyTab === 'users' && (
            <>
              {userActionError && (
                <div style={{
                  backgroundColor: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#f87171',
                  borderRadius: '6px',
                  padding: '10px 14px',
                  fontSize: '13px'
                }}>
                  {userActionError}
                </div>
              )}

              {/* Section A: Current Assigned Members Table */}
              <div>
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#ffffff', marginBottom: '10px', display: 'block' }}>
                  Current Company Operators ({companyUsers.length})
                </span>
                {usersLoading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                    Loading operators...
                  </div>
                ) : companyUsers.length === 0 ? (
                  <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    border: '1px dashed rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    padding: '24px',
                    textAlign: 'center',
                    color: '#94a3b8',
                    fontSize: '13px'
                  }}>
                    No operators or team members are currently assigned to this company.
                  </div>
                ) : (
                  <div style={{
                    backgroundColor: '#1e293b',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                      <thead>
                        <tr style={{
                          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                          backgroundColor: 'rgba(255, 255, 255, 0.02)',
                          color: '#cbd5e1',
                          fontSize: '11px',
                          textTransform: 'uppercase'
                        }}>
                          <th style={{ padding: '10px 14px' }}>Name & Email</th>
                          <th style={{ padding: '10px 14px' }}>Role</th>
                          <th style={{ padding: '10px 14px' }}>Status</th>
                          <th style={{ padding: '10px 14px', textAlign: 'right' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {companyUsers.map(u => (
                          <tr key={u.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.04)' }}>
                            <td style={{ padding: '10px 14px' }}>
                              <div style={{ fontWeight: 600, color: '#ffffff' }}>
                                {[u.first_name, u.last_name].filter(Boolean).join(' ') || 'Unnamed Operator'}
                              </div>
                              <div style={{ fontSize: '11px', color: '#94a3b8' }}>{u.email}</div>
                            </td>
                            <td style={{ padding: '10px 14px' }}>
                              <span style={{
                                backgroundColor: 'rgba(168, 85, 247, 0.15)',
                                color: '#c084fc',
                                border: '1px solid rgba(168, 85, 247, 0.3)',
                                borderRadius: '12px',
                                padding: '2px 8px',
                                fontSize: '11px',
                                fontWeight: 700
                              }}>
                                {u.role}
                              </span>
                            </td>
                            <td style={{ padding: '10px 14px' }}>
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                color: u.is_active ? '#34d399' : '#f87171',
                                fontSize: '12px',
                                fontWeight: 600
                              }}>
                                <span style={{
                                  width: '6px',
                                  height: '6px',
                                  borderRadius: '50%',
                                  backgroundColor: u.is_active ? '#34d399' : '#f87171'
                                }} />
                                {u.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                              <button
                                type="button"
                                onClick={() => handleRemoveUserFromCompany(u.id, u.email)}
                                title="Remove from this company"
                                style={{
                                  backgroundColor: 'rgba(239, 68, 68, 0.12)',
                                  border: '1px solid rgba(239, 68, 68, 0.25)',
                                  color: '#f87171',
                                  borderRadius: '6px',
                                  padding: '4px 8px',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  cursor: 'pointer'
                                }}
                              >
                                Unlink User
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Section B: Add / Assign User */}
              <div style={{
                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '14px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: '#ffffff' }}>
                    Add Operator to Company
                  </span>
                  <div style={{ display: 'flex', gap: '4px', background: '#1e293b', borderRadius: '6px', padding: '2px' }}>
                    <button
                      type="button"
                      onClick={() => setUserAssignMode('existing')}
                      style={{
                        backgroundColor: userAssignMode === 'existing' ? 'var(--primary-color)' : 'transparent',
                        color: userAssignMode === 'existing' ? '#ffffff' : '#94a3b8',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 10px',
                        fontSize: '11px',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      Assign Existing User
                    </button>
                    <button
                      type="button"
                      onClick={() => setUserAssignMode('new')}
                      style={{
                        backgroundColor: userAssignMode === 'new' ? 'var(--primary-color)' : 'transparent',
                        color: userAssignMode === 'new' ? '#ffffff' : '#94a3b8',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 10px',
                        fontSize: '11px',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      Create New Operator
                    </button>
                  </div>
                </div>

                {userAssignMode === 'existing' ? (
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '11px', color: '#cbd5e1' }}>Select Tenant User</label>
                      <select
                        value={selectedAssignUserId}
                        onChange={(e) => setSelectedAssignUserId(e.target.value)}
                        style={{
                          backgroundColor: '#1e293b',
                          border: '1px solid rgba(255, 255, 255, 0.15)',
                          color: '#ffffff',
                          borderRadius: '6px',
                          padding: '8px 10px',
                          fontSize: '12px',
                          width: '100%',
                          outline: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">-- Choose unassigned user --</option>
                        {tenantAvailableUsers.map(u => (
                          <option key={u.id} value={u.id}>
                            {[u.first_name, u.last_name].filter(Boolean).join(' ') || u.email} ({u.email}) - {u.role}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={handleAddUserToCompany}
                      disabled={!selectedAssignUserId}
                      style={{
                        backgroundColor: selectedAssignUserId ? 'var(--primary-color)' : '#334155',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: selectedAssignUserId ? 'pointer' : 'not-allowed',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Assign to Company
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleAddUserToCompany} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      <div>
                        <label style={{ fontSize: '11px', color: '#cbd5e1' }}>First Name</label>
                        <input
                          type="text"
                          placeholder="Jane"
                          value={newUserData.first_name}
                          onChange={(e) => setNewUserData(prev => ({ ...prev, first_name: e.target.value }))}
                          style={{
                            backgroundColor: '#1e293b',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                            color: '#ffffff',
                            borderRadius: '6px',
                            padding: '6px 10px',
                            fontSize: '12px',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '11px', color: '#cbd5e1' }}>Last Name</label>
                        <input
                          type="text"
                          placeholder="Doe"
                          value={newUserData.last_name}
                          onChange={(e) => setNewUserData(prev => ({ ...prev, last_name: e.target.value }))}
                          style={{
                            backgroundColor: '#1e293b',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                            color: '#ffffff',
                            borderRadius: '6px',
                            padding: '6px 10px',
                            fontSize: '12px',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      <div>
                        <label style={{ fontSize: '11px', color: '#cbd5e1' }}>Email Address *</label>
                        <input
                          type="email"
                          required
                          placeholder="operator@company.com"
                          value={newUserData.email}
                          onChange={(e) => setNewUserData(prev => ({ ...prev, email: e.target.value }))}
                          style={{
                            backgroundColor: '#1e293b',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                            color: '#ffffff',
                            borderRadius: '6px',
                            padding: '6px 10px',
                            fontSize: '12px',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '11px', color: '#cbd5e1' }}>Password *</label>
                        <input
                          type="password"
                          required
                          placeholder="Min 6 characters"
                          value={newUserData.password}
                          onChange={(e) => setNewUserData(prev => ({ ...prev, password: e.target.value }))}
                          style={{
                            backgroundColor: '#1e293b',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                            color: '#ffffff',
                            borderRadius: '6px',
                            padding: '6px 10px',
                            fontSize: '12px',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
                      <button
                        type="submit"
                        style={{
                          backgroundColor: 'var(--primary-color)',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '8px 18px',
                          fontSize: '12px',
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                      >
                        Create & Assign Operator
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </>
          )}

          {/* TAB 4: ACCOUNT & ORDERS HISTORY */}
          {activeCompanyTab === 'account' && (
            <>
              {/* Account KPIs */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '12px'
              }}>
                <div style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.02)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  padding: '12px 16px'
                }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-mute)', textTransform: 'uppercase', fontWeight: 600 }}>Total Spent</div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-main)', marginTop: '4px' }}>
                    ${(editingCompany.total_spent || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
                <div style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.02)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  padding: '12px 16px'
                }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-mute)', textTransform: 'uppercase', fontWeight: 600 }}>Coins Minted</div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#38bdf8', marginTop: '4px' }}>
                    {editingCompany.coins_count || 0}
                  </div>
                </div>
                <div style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.02)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  padding: '12px 16px'
                }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-mute)', textTransform: 'uppercase', fontWeight: 600 }}>Total Orders</div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#10b981', marginTop: '4px' }}>
                    {companyOrders.length}
                  </div>
                </div>
              </div>

              {/* Orders Table */}
              <div>
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#ffffff', marginBottom: '10px', display: 'block' }}>
                  Orders Placed by Company Operators
                </span>
                {ordersLoading ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                    Loading order history...
                  </div>
                ) : companyOrders.length === 0 ? (
                  <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    border: '1px dashed rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    padding: '40px',
                    textAlign: 'center',
                    color: '#94a3b8',
                    fontSize: '13px'
                  }}>
                    No orders have been placed by operators in this company yet.
                  </div>
                ) : (
                  <div style={{
                    background: 'var(--panel-bg, #1e293b)',
                    border: '1px solid var(--border-color, rgba(255, 255, 255, 0.1))',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                      <thead>
                        <tr style={{
                          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                          backgroundColor: 'rgba(255, 255, 255, 0.02)',
                          color: '#cbd5e1',
                          fontSize: '11px',
                          textTransform: 'uppercase'
                        }}>
                          <th style={{ padding: '10px 14px' }}>Order #</th>
                          <th style={{ padding: '10px 14px' }}>Date</th>
                          <th style={{ padding: '10px 14px' }}>Ordered By</th>
                          <th style={{ padding: '10px 14px' }}>Package / Items</th>
                          <th style={{ padding: '10px 14px', textAlign: 'center' }}>Coins</th>
                          <th style={{ padding: '10px 14px' }}>Status</th>
                          <th style={{ padding: '10px 14px', textAlign: 'right' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {companyOrders.map(ord => (
                          <tr key={ord.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.04)' }}>
                            <td style={{ padding: '10px 14px', fontWeight: 700, color: '#00c6ff' }}>
                              #{ord.id}
                            </td>
                            <td style={{ padding: '10px 14px', color: '#cbd5e1', fontSize: '12px' }}>
                              {ord.created_at ? new Date(ord.created_at).toLocaleDateString() : '—'}
                            </td>
                            <td style={{ padding: '10px 14px' }}>
                              <div style={{ fontWeight: 600, color: '#ffffff', fontSize: '12px' }}>
                                {ord.ordered_by_user_name || 'Operator'}
                              </div>
                              <div style={{ fontSize: '11px', color: '#94a3b8' }}>{ord.ordered_by_user_email}</div>
                            </td>
                            <td style={{ padding: '10px 14px', color: '#ffffff', fontSize: '12px', fontWeight: 500 }}>
                              {ord.package_name || 'Custom Coin Order'}
                            </td>
                            <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                              <span style={{
                                backgroundColor: 'rgba(0, 198, 255, 0.1)',
                                color: '#38bdf8',
                                borderRadius: '12px',
                                padding: '2px 8px',
                                fontSize: '11px',
                                fontWeight: 700
                              }}>
                                {ord.coins_count || 1}
                              </span>
                            </td>
                            <td style={{ padding: '10px 14px' }}>
                              <span style={{
                                backgroundColor: ord.status === 'completed' || ord.status === 'shipped'
                                  ? 'var(--success-bg, rgba(16, 185, 129, 0.15))'
                                  : ord.status === 'in_production'
                                  ? 'var(--info-bg, rgba(56, 189, 248, 0.15))'
                                  : 'var(--warning-bg, rgba(245, 158, 11, 0.15))',
                                color: ord.status === 'completed' || ord.status === 'shipped'
                                  ? 'var(--success-text, #15803d)'
                                  : ord.status === 'in_production'
                                  ? 'var(--info-text, #0369a1)'
                                  : 'var(--warning-text, #b45309)',
                                border: '1px solid var(--border-color, rgba(255, 255, 255, 0.1))',
                                borderRadius: '12px',
                                padding: '2px 8px',
                                fontSize: '11px',
                                fontWeight: 700,
                                textTransform: 'capitalize'
                              }}>
                                {ord.status ? ord.status.replace(/_/g, ' ') : 'Pending'}
                              </span>
                            </td>
                            <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: 'var(--text-main, #ffffff)' }}>
                              ${Number(ord.total_price || 0).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
            </div>

            {/* Modal Actions Footer */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '14px 20px',
              borderTop: '1px solid var(--border-color)',
              backgroundColor: 'rgba(255, 255, 255, 0.02)'
            }}>
              <div>
                {!isNewCompany && editingCompany && (
                  <span style={{ fontSize: '12px', color: 'var(--text-mute)' }}>
                    Active company: <strong style={{ color: 'var(--text-main)' }}>{editingCompany.name}</strong>
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button
                  type="button"
                  data-testid="company-modal-cancel-btn"
                  onClick={() => {
                    setEditingCompany(null);
                  }}
                  style={{
                    backgroundColor: 'var(--input-bg)',
                    border: '1px solid var(--border-color)',
                    color: 'var(--text-mute)',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  {activeCompanyTab === 'users' || activeCompanyTab === 'account' ? 'Close' : 'Cancel'}
                </button>
                {(activeCompanyTab === 'details' || activeCompanyTab === 'address' || isNewCompany) && (
                  <button
                    type="button"
                    onClick={handleSaveCompany}
                    disabled={companyModalLoading}
                    data-testid="save-company-btn"
                    style={{
                      backgroundColor: 'var(--primary-color)',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 20px',
                      fontSize: '13px',
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    {companyModalLoading ? 'Saving...' : (isNewCompany ? 'Create Company' : 'Save Changes')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ========================================================================= */}
      {/* MODAL 4: DELETE CONFIRMATION                                              */}
      {/* ========================================================================= */}
      {deletingCompany && createPortal(
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(5, 8, 16, 0.85)',
          backdropFilter: 'blur(12px)',
          zIndex: 999999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--card-bg, #111622)',
            border: '1px solid rgba(239, 68, 68, 0.35)',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '460px',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.6)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: 'rgba(239, 68, 68, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ef4444'
              }}>
                <Icons.Trash size={20} />
              </div>
              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: 'var(--text-main)' }}>
                Delete Company
              </h3>
            </div>

            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-mute)', lineHeight: '1.6' }}>
              Are you sure you want to delete <strong style={{ color: 'var(--text-main)' }}>"{deletingCompany.name}"</strong>? 
              This will remove the company registration and unlink all assigned operators. Orders placed under this company will remain preserved.
            </p>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
              <button
                type="button"
                onClick={() => setDeletingCompany(null)}
                style={{
                  backgroundColor: 'var(--input-bg)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-mute)',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '13px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteCompany}
                disabled={deleteLoading}
                style={{
                  backgroundColor: '#ef4444',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 18px',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                {deleteLoading ? 'Deleting...' : 'Delete Company'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
}
